import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {NavItem} from '../types';

/**
 * Manages transient UI state including column visibility, modals, tabs, file renaming, and navigation items.
 * Handles ephemeral user interface state that does not persist across sessions.
 */
export class UIStateStore {
    // Observable state
    additionalProviderVisibleColumns: string[] = [];
    additionalProviderPendingColumns: string[] = [];
    showRevertModal = false;
    activeTab = 0;
    renamingFileId: string | null = null;
    existingProviderQueuedForDeletion: string | null = null;
    navItems: NavItem[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    // Setters
    setAdditionalProviderVisibleColumns(columns: string[]): void {
        this.additionalProviderVisibleColumns = columns;
    }

    setAdditionalProviderPendingColumns(columns: string[]): void {
        this.additionalProviderPendingColumns = columns;
    }

    setShowRevertModal(show: boolean): void {
        this.showRevertModal = show;
    }

    setActiveTab(index: number): void {
        this.activeTab = index;
    }

    setRenamingFileId(fileId: string | null): void {
        this.renamingFileId = fileId;
    }

    setExistingProviderQueuedForDeletion(providerKey: string | null): void {
        this.existingProviderQueuedForDeletion = providerKey;
    }

    setNavItems(items: NavItem[]): void {
        this.navItems = items;
    }

    async loadHeaders(controller?: AbortController): Promise<void> {
        try {
            const response = await networkOptimization.fetchNavItems();
            runInAction(() => {
                this.navItems = response;
            });
        } catch (error) {
            if (this.isAbortError(error)) return;
            throw error;
        }
    }

    private isAbortError(error: unknown): boolean {
        return error instanceof Error && error.name === 'AbortError';
    }

    // Reset
    resetUIState(): void {
        this.additionalProviderVisibleColumns = [];
        this.additionalProviderPendingColumns = [];
        this.showRevertModal = false;
        this.activeTab = 0;
        this.renamingFileId = null;
        this.existingProviderQueuedForDeletion = null;
        this.navItems = [];
    }
}
